import { ChangeDetectionStrategy, Component, computed, inject, Injector, input, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import {
  faAngleDown,
  faCheck,
  faEnvelope,
  faInfoCircle,
  faMapMarkedAlt,
  faMapMarkerAlt,
  faTimes,
  faUser,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { ModalController, Platform } from '@ionic/angular/lazy';
import { filter, map, merge, NEVER, of, switchMap, tap, timer } from 'rxjs';
import { CurrentCreneauService } from 'src/app/services/current-creneau.service';
import { Coordinates } from '../../entities/coordinates.model';
import { CreneauInterPonctuelle } from '../../entities/creneaux/creneau-inter-ponctuelle.model';
import { InterPonctuelleForResponsableDTO } from '../../entities/demande/inter-ponctuelle-for-responsable.dto';
import { NotificationInteractionService } from '../../notification-alerte/domain/notification-interaction.service';
import { NotificationMissionUpdateSiteClient } from '../../notification-alerte/domain/notification-mission.model';
import { GeolocationService } from '../../services/geolocation.service';
import { ImageGalleryModal } from '../image-gallery-modal/image-gallery.modal';
import { ouvrirItineraire } from '../itineraire';

@Component({
  selector: 'in2-details-demande-common',
  templateUrl: './details-demande-common.component.html',
  styleUrls: ['./details-demande-common.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DetailsDemandeCommonComponent {
  public readonly MAX_THUMBNAILS = 3;

  private readonly currentCreneauService = inject(CurrentCreneauService);
  private readonly modalController = inject(ModalController);
  public readonly sanitizer = inject(DomSanitizer);
  private readonly geolocationService = inject(GeolocationService);
  private readonly firebaseNotificationCenter = inject(NotificationInteractionService);
  private readonly platform = inject(Platform);
  private readonly injector = inject(Injector);

  // Inputs
  public readonly demandeInter = input<InterPonctuelleForResponsableDTO>();
  public readonly creneau = input<CreneauInterPonctuelle>();

  // Icons
  public readonly faEnvelope: IconDefinition = faEnvelope;
  public readonly faMapMarkerAlt: IconDefinition = faMapMarkerAlt;
  public readonly faMapMarkedAlt: IconDefinition = faMapMarkedAlt;
  public readonly faInfoCircle: IconDefinition = faInfoCircle;
  public readonly faAngleDown: IconDefinition = faAngleDown;
  public readonly faUser: IconDefinition = faUser;
  public readonly faTimes: IconDefinition = faTimes;
  public readonly faCheck: IconDefinition = faCheck;

  public readonly isRefreshing = signal(false);

  private readonly creneauInputId = computed(() => this.demandeInter()?.prestationId ?? this.creneau()?.prestationId);

  private readonly refreshedCreneau = toSignal(
    this.firebaseNotificationCenter.notificationTapped$.pipe(
      filter((notif): notif is NotificationMissionUpdateSiteClient => notif instanceof NotificationMissionUpdateSiteClient),
      filter(notif => notif.idDemande === this.creneauInputId()),
      tap(() => this.isRefreshing.set(true)),
      switchMap(() => this.currentCreneauService.refreshCreneau()),
      map(() => this.currentCreneauService.getCurrentInterPonctuelle()),
      tap(() => this.isRefreshing.set(false))
    )
  );

  public readonly demande = computed(() => {
    return this.refreshedCreneau() ?? this.demandeInter() ?? this.creneau();
  });

  public readonly distanceInMeters = toSignal(
    merge(this.platform.resume.pipe(map(() => true)), this.platform.pause.pipe(map(() => false)), of(true)).pipe(
      switchMap(active => (active ? timer(0, 60000) : NEVER)),
      switchMap(() => toObservable(this.demande, { injector: this.injector })),
      switchMap(demande => {
        if (!demande?.site?.location) return of(undefined);
        return this.geolocationService
          .getCoordinates(true)
          .pipe(map(coords => (coords ? Coordinates.getDistance(coords, demande.site?.location as Coordinates) : undefined)));
      })
    )
  );

  public readonly dateEmission = computed(() => {
    const d = this.demande();
    if (!d) return undefined;
    if (d instanceof InterPonctuelleForResponsableDTO) {
      return d.date;
    } else {
      return (d as CreneauInterPonctuelle).instant?.toDate();
    }
  });

  public readonly autorisationPenetrerInterieurBatiment = computed(() => {
    const d = this.demande();
    if (!d) return undefined;
    return d instanceof InterPonctuelleForResponsableDTO
      ? d.autorisationPenetrerInterieurBatiment
      : (d as CreneauInterPonctuelle).precisionSite?.autorisationPenetrerInterieurBatiment;
  });

  public readonly autorisationPenetrerEspacesExterieurCloture = computed(() => {
    const d = this.demande();
    if (!d) return undefined;
    return d instanceof InterPonctuelleForResponsableDTO
      ? d.autorisationPenetrerEspacesExterieurCloture
      : (d as CreneauInterPonctuelle).precisionSite?.autorisationPenetrerEspacesExterieurCloture;
  });

  public readonly autorisationPenetrerEspacesCommun = computed(() => {
    const d = this.demande();
    if (!d) return undefined;
    return d instanceof InterPonctuelleForResponsableDTO
      ? d.autorisationPenetrerEspacesCommun
      : (d as CreneauInterPonctuelle).precisionSite?.autorisationPenetrerEspacesCommun;
  });

  public readonly shouldDisplayAcces = computed(() => {
    const d = this.demande();
    if (!d) return false;
    return d instanceof InterPonctuelleForResponsableDTO || !!(d as CreneauInterPonctuelle).vacation?.arriveeSurSite;
  });

  public openMap(latitude: number, longitude: number, adresse: string | undefined) {
    ouvrirItineraire(latitude, longitude, adresse);
  }

  public async openPictureModal(index: number) {
    const d = this.demande();
    if (!d?.precisionSite?.photos) return;

    const modal = await this.modalController.create({
      component: ImageGalleryModal,
      componentProps: {
        externalUrls: d.precisionSite.photos,
        startIndex: index,
      },
      backdropDismiss: false,
    });

    await modal.present();
  }
}
